import React, { useState } from 'react'
import { Trash2, RefreshCcw, ImageIcon } from 'lucide-react'
import { gql } from 'graphql-request'

import { useMutation } from '@hooks/graphql'
import { useFlashMessage } from '@components/FlashMessage'
import Card from '@components/Card'
import Image from '@components/Image'
import { Button } from '@components/ui/button'
import Loader from '@components/Loader'
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip'
import SingleFileUpload from '@components/SingleFileInput'
import ImageGenerationProgress from '../ImageGenerationProgress'
import { TextShimmer } from '@/components/ui/text-shimmer'

const CREATE_IMAGE_MUTATION = gql`
  mutation createImage($input: CreateImageInput!) {
    createImage(input: $input) {
      image {
        url
        filename
      }
    }
  }
`

const DELETE_IMAGE_MUTATION = gql`
  mutation deleteImage($input: DeleteImageInput!) {
    deleteImage(input: $input) {
      success
    }
  }
`

const GENERATE_IMAGE_MUTATION = gql`
  mutation generateTutorImage($input: GenerateImageInput!) {
    generateTutorImage(input: $input) {
      tutor {
        imageGenerationStatus
      }
    }
  }
`

const TutorImage = ({
  tutorId,
  isOwner,
  imageGenerationStatus,
  imageGenerationStartedAt,
  imageUrl,
  refetch
}) => {
  const [showImageSizeError, setShowImageSizeError] = useState(false)
  const { setFlashMessage } = useFlashMessage()

  const { mutate: createImage, isLoading: isCreating } = useMutation({
    gqlMutation: CREATE_IMAGE_MUTATION,
    onSuccess: () => refetch()
  })

  const { mutate: deleteImage, isLoading: isDeleting } = useMutation({
    gqlMutation: DELETE_IMAGE_MUTATION,
    variables: { input: { id: tutorId } },
    onSuccess: () => refetch()
  })

  const { mutate: regenerateImage, isLoading: isRegenerating } = useMutation({
    gqlMutation: GENERATE_IMAGE_MUTATION,
    variables: { input: { id: tutorId } },
    onSuccess: () => refetch(),
    onError: (error) => {
      if (error.response?.errors?.[0]?.message === 'Query rate limit exceeded') {
        setFlashMessage('You have reached the maximum number of image generations for today. Please try again tomorrow.', 'error')
      } else {
        setFlashMessage('Something went wrong', 'error')
      }
    }
  })

  return (
    <Card className='flex flex-col p-0 mb-3 h-fit overflow-crop w-[200px] lg:w-[250px] shrink-0'>
      <Choose>

        <When condition={imageGenerationStatus === 'generating'}>
          <div className='w-full h-[200px] lg:h-[250px] rounded-lg bg-input flex flex-col items-center justify-center p-3'>
            <TextShimmer className='self-center mb-2' duration={1}>
              Creating image
            </TextShimmer>
            <ImageGenerationProgress startedAt={imageGenerationStartedAt} />
          </div>
        </When>

        <When condition={isCreating}>
          <Loader className='self-center my-24' />
        </When>

        <When condition={imageUrl}>
          <div className='relative group'>
            <Image
              className='object-cover w-full h-[200px] lg:h-[250px] md:rounded-lg rounded-none'
              src={imageUrl}
              placeholder={<div className='md:rounded-lg rounded-none h-[200px] w-full bg-gray-200 animate-pulse border shadow-sm' />}
            />

            <If condition={isOwner}>
              <div className='absolute flex bottom-0 right-0 m-3 gap-2'>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className='hover:bg-accent dark:hover:bg-accent dark:bg-accent'
                      variant='outline'
                      size='icon'
                      onClick={() => regenerateImage()}
                      id='regenerate-image'
                      aria-label='Regenerate image'>
                      <RefreshCcw size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Generate a new image</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className='hover:bg-destructive dark:hover:bg-destructive dark:bg-destructive'
                      onClick={() => deleteImage()}
                      variant='destructive'
                      size='icon'
                      id='delete-image'
                      aria-label='Delete image'>
                      <Trash2 size={16} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete this image</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </If>
          </div>
        </When>

        <When condition={isOwner}>
          <div className='bg-input flex flex-col items-center justify-center py-10 px-5'>
            <Button
              variant='outline'
              className='self-start'
              onClick={() => regenerateImage()}
            >
              <ImageIcon size={16} className='mr-2' />
              Generate an image
            </Button>

            <SingleFileUpload
              label='Upload an image'
              className='mt-3 text-center w-full'
              buttonClassName='w-full'
              tabIndex={0}
              theme='secondary'
              accept='.png,.jpg,.jpeg'
              onChange={ev => {
                const file = ev.target.files[0]

                if (file && file.size > 10000000) { // 10mb limit
                  setShowImageSizeError(true)

                  return
                }

                createImage({ input: { id: tutorId, file } })
              }}
            />
            <small className='mt-3 leading-tight'>
              Supported file formats: .png, .jpg and .gif.
              Files must be smaller than 10mb.
            </small>

            <If condition={showImageSizeError}>
              <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>File size must be less than 10mb</p>
            </If>
          </div>
        </When>

        <Otherwise>
          <div className='bg-gray-200 h-[200px]' />
        </Otherwise>
      </Choose>
    </Card>
  )
}

export default TutorImage
